import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import SignupTrendChart from './SignupTrendChart';
import './KYCManagement.css';
import './SelfRegisteredAgents.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'awaiting_kyc_upload', label: 'IRA licence not uploaded' },
  { value: 'ready_for_review', label: 'Ready for review' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' }
];

const SelfRegisteredAgents = ({ onShowNotification }) => {
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_BASE_URL = getApiBaseUrl();

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const loadSummary = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/self-registered-agents/summary`, {
        headers: authHeaders()
      });
      const result = await response.json();
      if (response.ok && result.success) setSummary(result);
    } catch (error) {
      console.error('Error loading self-registered summary:', error);
    }
  };

  const loadAgents = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: '20',
        status: statusFilter
      });
      if (searchTerm) params.append('search', searchTerm);
      const response = await fetch(`${API_BASE_URL}/api/admin/self-registered-agents?${params}`, {
        headers: authHeaders()
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setAgents(result.agents || []);
        setPagination(result.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
      } else if (onShowNotification) {
        onShowNotification(result.error || 'Failed to load Sign Up agents', 'error');
      }
    } catch (error) {
      console.error('Error loading self-registered agents:', error);
      if (onShowNotification) onShowNotification('Error loading Sign Up agents.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    loadAgents();
  }, [currentPage, searchTerm, statusFilter]);

  const loadAgentDetails = async (agentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/self-registered-agents/${agentId}`, {
        headers: authHeaders()
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setAgentDetails(result.agent);
        setDocuments(result.documents || []);
        setSelectedAgent(agentId);
      } else if (onShowNotification) {
        onShowNotification(result.error || 'Failed to load agent details', 'error');
      }
    } catch (error) {
      console.error('Error loading agent details:', error);
      if (onShowNotification) onShowNotification('Error loading agent details.', 'error');
    }
  };

  const handleReviewAgent = async () => {
    if (!selectedAgent || !reviewStatus) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/self-registered-agents/${selectedAgent}/review`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kyc_status: reviewStatus, review_notes: reviewNotes })
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        if (onShowNotification) onShowNotification(`KYC ${reviewStatus} successfully.`, 'success');
        setShowReviewModal(false);
        setReviewNotes('');
        loadSummary();
        loadAgents();
        loadAgentDetails(selectedAgent);
      } else {
        throw new Error(result.error || 'Failed to update KYC status');
      }
    } catch (error) {
      if (onShowNotification) onShowNotification(error.message || 'Error updating KYC status.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDocument = async (docId, documentName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/self-registered-agents/documents/${docId}/download`, {
        headers: authHeaders()
      });
      if (!response.ok) throw new Error('Failed to download document');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = documentName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (onShowNotification) onShowNotification('Error viewing document.', 'error');
    }
  };

  const getStatusBadge = (status, label) => {
    const map = {
      awaiting_kyc_upload: { bg: '#fff3cd', color: '#856404', text: 'IRA licence not uploaded' },
      kyc_uploaded_waiting_period: { bg: '#cce5ff', color: '#004085', text: 'Ready for review' },
      ready_for_review: { bg: '#cce5ff', color: '#004085', text: 'Ready for review' },
      kyc_incomplete_review_due: { bg: '#fff3cd', color: '#856404', text: 'IRA licence not uploaded' },
      approved: { bg: '#d4edda', color: '#155724', text: 'Approved' },
      declined: { bg: '#f8d7da', color: '#721c24', text: 'Declined' },
      flagged: { bg: '#ffeaa7', color: '#6d4c00', text: 'Flagged' },
      pending: { bg: '#fff3cd', color: '#856404', text: 'Pending' }
    };
    const config = map[status] || { bg: '#e2e8f0', color: '#334155', text: label || status || 'Unknown' };
    return (
      <span className="self-reg-status-badge" style={{ background: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  const counts = summary?.counts || {};
  const tracker = summary?.tracker;
  const canReview = agentDetails?.review_eligible && agentDetails?.kyc_status !== 'approved';

  return (
    <div className="kyc-management-container self-registered-agents">
      <div className="kyc-header">
        <div className="header-left">
          <h2>Self-Registered Agents</h2>
          <p>Sign Up accounts only. Credit Control can approve or decline after the IRA licence is uploaded. Extra files already on an account are kept.</p>
        </div>
      </div>

      {tracker && Array.isArray(tracker.series) && (
        <div className="self-reg-tracker">
          <div className="self-reg-tracker-head">
            <div>
              <h3>Daily Sign Ups since the flyer</h3>
              <p>Counts only. Campaign start {tracker.flyerStart}. Today {tracker.todayCount || 0} · since flyer {tracker.sinceFlyer || 0}.</p>
            </div>
          </div>
          <SignupTrendChart series={tracker.series} height={260} />
        </div>
      )}

      <div className="self-reg-summary-grid">
        <div className="self-reg-summary-card">
          <span className="label">Total Sign Up accounts</span>
          <strong>{counts.total || 0}</strong>
        </div>
        <div className="self-reg-summary-card">
          <span className="label">IRA licence not uploaded</span>
          <strong>{counts.awaiting_kyc_upload || 0}</strong>
        </div>
        <div className="self-reg-summary-card">
          <span className="label">Ready for review</span>
          <strong>{(counts.ready_for_review || 0) + (counts.kyc_incomplete_review_due || 0)}</strong>
        </div>
        <div className="self-reg-summary-card">
          <span className="label">Approved</span>
          <strong>{counts.approved || 0}</strong>
        </div>
        <div className="self-reg-summary-card">
          <span className="label">Declined</span>
          <strong>{counts.declined || 0}</strong>
        </div>
      </div>

      <div className="kyc-filter-bar">
        <div className="filter-bar-content">
          <div className="filter-group">
            <label htmlFor="search-self-reg" className="filter-label">Search agents</label>
            <input
              type="text"
              id="search-self-reg"
              placeholder="Search by name, IRA, email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="status-self-reg" className="filter-label">Account status</label>
            <select
              id="status-self-reg"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="kyc-content">
        <div className="agents-list-panel">
          <h3>Sign Up agents ({pagination.total})</h3>
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading agents...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="no-data">No Sign Up agents found</div>
          ) : (
            <div className="agents-list">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className={`agent-card ${selectedAgent === agent.id ? 'selected' : ''}`}
                  onClick={() => loadAgentDetails(agent.id)}
                >
                  <div className="agent-card-header">
                    <h4>{agent.agency_name}</h4>
                    {getStatusBadge(agent.account_status, agent.account_status_label)}
                  </div>
                  <div className="agent-card-body">
                    <div className="agent-info-row">
                      <span className="label">IRA Number:</span>
                      <span className="value">{agent.ira_number}</span>
                    </div>
                    <div className="agent-info-row">
                      <span className="label">Email:</span>
                      <span className="value">{agent.email || 'N/A'}</span>
                    </div>
                    <div className="agent-info-row">
                      <span className="label">Signed up:</span>
                      <span className="value">
                        {agent.self_registered_at ? new Date(agent.self_registered_at).toLocaleDateString('en-KE') : 'N/A'}
                      </span>
                    </div>
                    <div className="agent-info-row">
                      <span className="label">Documents:</span>
                      <span className="value">{agent.total_documents || 0} uploaded</span>
                    </div>
                    {!agent.review_eligible && (
                      <div className="agent-info-row">
                        <span className="label">Review:</span>
                        <span className="value">Waiting for IRA licence</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
              <span>Page {currentPage} of {pagination.totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={currentPage === pagination.totalPages}>Next</button>
            </div>
          )}
        </div>

        <div className="agent-details-panel">
          {selectedAgent && agentDetails ? (
            <>
              <div className="agent-details-header">
                <h3>{agentDetails.agency_name}</h3>
                <button
                  className="review-btn"
                  onClick={() => setShowReviewModal(true)}
                  disabled={!canReview}
                  title={agentDetails.review_eligible ? '' : 'Review after the IRA licence is uploaded'}
                >
                  {agentDetails.kyc_status === 'approved' ? 'Approved' : (canReview ? 'Review KYC' : 'Waiting for IRA licence')}
                </button>
              </div>

              <div className="agent-details-content">
                <div className="details-section">
                  <h4>Account information</h4>
                  <div className="details-grid">
                    <div className="detail-item"><span className="label">IRA Number:</span><span className="value">{agentDetails.ira_number}</span></div>
                    <div className="detail-item"><span className="label">Username:</span><span className="value">{agentDetails.username}</span></div>
                    <div className="detail-item"><span className="label">Email:</span><span className="value">{agentDetails.email || 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">Phone:</span><span className="value">{agentDetails.phone || 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">Full name:</span><span className="value">{agentDetails.full_names || 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">ID Number:</span><span className="value">{agentDetails.id_number || 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">KRA PIN:</span><span className="value">{agentDetails.kra_pin || 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">Agent type:</span><span className="value">{agentDetails.kyc_legal_type === 'business_entity' ? 'Business entity' : (agentDetails.kyc_legal_type === 'sole_proprietor' ? 'Sole proprietor' : 'Not selected')}</span></div>
                    {agentDetails.kyc_legal_type === 'business_entity' && (
                      <div className="detail-item"><span className="label">Business name:</span><span className="value">{agentDetails.kyc_business_name || 'N/A'}</span></div>
                    )}
                    <div className="detail-item"><span className="label">Account status:</span><span className="value">{getStatusBadge(agentDetails.account_status, agentDetails.account_status_label)}</span></div>
                    <div className="detail-item"><span className="label">Signed up:</span><span className="value">{agentDetails.self_registered_at ? new Date(agentDetails.self_registered_at).toLocaleString('en-KE') : 'N/A'}</span></div>
                    <div className="detail-item"><span className="label">Credit Control:</span><span className="value">{agentDetails.review_eligible ? 'Can review now' : 'Waiting for IRA licence'}</span></div>
                  </div>
                </div>

                <div className="documents-section">
                  <h4>KYC documents</h4>
                  {documents.length === 0 ? (
                    <div className="no-data">No documents uploaded yet</div>
                  ) : (
                    <div className="documents-list">
                      {documents.map((doc) => (
                        <div key={doc.id} className="document-card">
                          <div className="document-header">
                            <span className="document-type">{String(doc.document_type || '').replace(/_/g, ' ')}</span>
                          </div>
                          <div className="document-body">
                            <div className="document-info">
                              <span className="document-name">{doc.document_name}</span>
                              <span className="document-size">{doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : ''}</span>
                            </div>
                            <div className="document-actions">
                              <button onClick={() => handleViewDocument(doc.id, doc.document_name)} className="view-doc-btn">
                                View document
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h3>Select a Sign Up agent</h3>
              <p>View account status, uploaded documents, and approve or decline after the IRA licence is uploaded.</p>
            </div>
          )}
        </div>
      </div>

      {showReviewModal && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Sign Up KYC</h3>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Decision</label>
                <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)} className="form-select">
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                  <option value="flagged">Flagged</option>
                </select>
              </div>
              <div className="form-group">
                <label>Review notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes for the agent"
                  rows="4"
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowReviewModal(false); setReviewNotes(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleReviewAgent} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfRegisteredAgents;
